import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analyticsPromise } from './firebase';

/**
 * DLP Tracker Analytics
 *
 * All events flow to Google Analytics 4 (GA4) via Firebase.
 * View data at: https://analytics.google.com
 *   or: https://console.firebase.google.com/project/dlp-tracker/analytics
 *
 * Every function is fire-and-forget — if analytics isn't supported
 * (e.g., ad-blocked, SSR), calls silently no-op.
 */

let analytics = null;
analyticsPromise.then(a => { analytics = a; });

function log(eventName, params) {
  if (!analytics) return;
  try {
    logEvent(analytics, eventName, params);
  } catch {
    // Silently ignore — analytics should never break the app
  }
}

// ── User identity ──────────────────────────────────────────────

export function identifyUser(user) {
  if (!analytics || !user) return;
  setUserId(analytics, user.uid);
  setUserProperties(analytics, {
    auth_method: user.providerData?.[0]?.providerId || 'unknown',
  });
}

export function clearUserIdentity() {
  if (!analytics) return;
  setUserId(analytics, null);
}

// ── Navigation ─────────────────────────────────────────────────

export function trackTabView(tabName) {
  log('screen_view', {
    screen_name: tabName,
    screen_class: 'TrackerApp',
  });
}

// ── Ride / item tracking ───────────────────────────────────────

export function trackItemChecked(itemName, checked) {
  log(checked ? 'item_checked' : 'item_unchecked', {
    item_name: itemName,
  });
}

export function trackPhotoUpload(itemName) {
  log('photo_upload', {
    item_name: itemName,
  });
}

export function trackNoteAdded(itemName) {
  log('note_added', {
    item_name: itemName,
  });
}

// ── Multi-visit tracking ──────────────────────────────────────

export function trackVisitIncremented(itemName, newCount) {
  log('visit_incremented', {
    item_name: itemName,
    visit_count: newCount,
  });
}

export function trackVisitDecremented(itemName, newCount) {
  log('visit_decremented', {
    item_name: itemName,
    visit_count: newCount,
  });
}

// ── Wishlist ───────────────────────────────────────────────────

export function trackWishlistToggle(itemName, wishlisted) {
  log(wishlisted ? 'wishlist_add' : 'wishlist_remove', {
    item_name: itemName,
  });
}

// ── Search ─────────────────────────────────────────────────────

export function trackSearch(query, resultCount) {
  log('search', {
    search_term: query,
    results_count: resultCount,
  });
}

// ── Wait times ─────────────────────────────────────────────────

export function trackWaitTimesView() {
  log('wait_times_view', {});
}

export function trackWaitTimesRefresh() {
  log('wait_times_refresh', {});
}

// ── Notification alerts ────────────────────────────────────────

export function trackAlertSet(rideName, threshold) {
  log('alert_set', {
    ride_name: rideName,
    threshold_minutes: threshold,
  });
}

export function trackAlertRemoved(rideName) {
  log('alert_removed', {
    ride_name: rideName,
  });
}

export function trackAlertFired(rideName, waitTime, threshold) {
  log('alert_fired', {
    ride_name: rideName,
    wait_time: waitTime,
    threshold_minutes: threshold,
  });
}

// ── Auth ───────────────────────────────────────────────────────

export function trackLogin(method) {
  log('login', { method });
}

export function trackSignup(method) {
  log('sign_up', { method });
}

// ── Leaderboard ────────────────────────────────────────────────

export function trackLeaderboardView() {
  log('leaderboard_view', {});
}

// ── Dark mode ──────────────────────────────────────────────────

export function trackDarkModeToggle(enabled) {
  log('dark_mode_toggle', { enabled });
}

// ── Affiliate / Book Your Trip ──────────────────────────────────

export function trackBookTripView() {
  log('book_trip_view', {});
}

export function trackAffiliateClick(partner, linkLabel) {
  log('affiliate_click', {
    affiliate_partner: partner,
    link_label: linkLabel,
  });
}
